<i18n>
{
  "en": {
    "users": "Users",
    "createUserRoleError": "Error creating user. Please, try again later.",
    "createUserRoleSuccess": "Created user \"{0}\" !",
    "editUserRoleSuccess": "User \"{0}\" was successfully edited!",
    "editUserRoleError": "Error editing user \"{0}\". Please, try again later.",
    "deleteUserRoleTitle": "Do you want to delete user: \"{0}\" ?",
    "deleteUserRoleDescription": "This can not be undone",
    "delete": "Delete",
    "deleteUserRoleSuccess": "User \"{0}\" was deleted",
    "deleteUserRoleError": "Error deleting user \"{0}\". Please, try again later.",
    "regeneratePassUserRoleSuccess": "The password has been sent to the user",
    "regeneratePassUserRoleError": "Error in regenerate the password",
    "add": "Add"
  },
  "es": {
    "users": "Usuarios",
    "createUserRoleError": "Error creando el usuario. Inténtelo de nuevo más tarde.",
    "createUserRoleSuccess": "Creado el usuario \"{0}\" !",
    "editUserRoleSuccess": "El usuario \"{0}\" se editó correctamente",
    "editUserRoleError": "Error editando el usuario \"{0}\". Inténtelo de nuevo más tarde.",
    "deleteUserRoleTitle": "¿Desea borrar el usuario \"{0}\" ?",
    "deleteUserRoleDescription": "Este cambio es irreversible",
    "delete": "Eliminar",
    "deleteUserRoleSuccess": "El usuario \"{0}\" fue borrado",
    "deleteUserRoleError": "Error al borrar el usuario \"{0}\". Inténtelo de nuevo más tarde.",
    "regeneratePassUserRoleSuccess":  "La contraseña ha sido enviada al usuario",
    "regeneratePassUserRoleError":  "Error al regenerar la contraseña",
    "add":  "Añadir"
  }
}
</i18n>
<template>
  <div>
    <div class="d-flex flex-wrap mx-7 mt-3 mb-3">
      <v-row>
        <v-col cols="12" align-self="center" class="pa-0 my-4">
          <div class="text-center text-h5">
            {{ $t('users') }}
          </div>
        </v-col>
        <user-list-filter
          @pointOfSale="onPointOfSaleFilter" @userRole="onUserRoleFilter"
          @userRoleLevel="onUserRoleLevelFilter"
          @userActive="onUserActiveFilter"
          @userSearch="onUserSearch"
        ></user-list-filter>
        <v-col align-self="end" class="pa-0 te">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon class="plus-icon mr-0" v-on="on" v-bind="attrs"
                     @click="openModal">
                <v-icon color="grey" large>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('add') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
    <v-divider/>
    <v-list>
      <div v-if="filteredUserRoleList">
        <user-row v-for="userRole in filteredUserRoleList" :key="userRole.username" :userRole="userRole"
                  @edit="onEdit(userRole)"
                  @delete="onDelete(userRole)"
                  @regeneratePassword="onRegeneratePassword(userRole)"
        >
        </user-row>
      </div>
    </v-list>
    <user-role-create-edit-modal
      :visible="createUserModal"
      @confirm="onConfirmUserRole"
      @cancel="onCloseModal"
      :userRole="userRoleToCreate"
      type="create"
    />
    <user-role-create-edit-modal
      :visible="editUserRoleModal"
      @confirm="onEditUserRole"
      @cancel="onCloseModal"
      :userRole="userRole"
      type="edit"
    />
    <dynameat-confirmation-modal :visible="userRoleDeleteModal"
                                 :title="$t('deleteUserRoleTitle', [userRoleToDeleteTitle])"
                                 :description="$t('deleteUserRoleDescription')"
                                 :confirm-text="$t('delete')" @confirm="onDeleteUserRole"
                                 @cancel="onCloseModal"/>
  </div>
</template>
<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import { UserRole } from '@userManager/models';
import { Watch } from 'vue-property-decorator';
import { createErrorNotification, createSuccessNotification } from '@ulibs/unnotificationsqueue';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import { normalizeStr } from '@core/helpers';
import UserRow from '../components/userRow.vue';
import UserRoleCreateEditModal from '../components/UserRoleCreateEditModal.vue';
import UserListFilter from '../components/UserListFilter.vue';

@Component
@Components({
  UserRow,
  UserRoleCreateEditModal,
  DynameatConfirmationModal,
  UserListFilter
})
@MapActions(['userRoleListRequest', 'createUserRoleRequest', 'updateUserRoleRequest', 'addNotification', 'deleteUserRoleRequest', 'regeneratePasswordUserRoleRequest'])
@MapGetters(['userRoleList', 'createUserRoleSuccess', 'updateUserRoleSuccess', 'deleteUserRoleSuccess', 'userSettings'])
export default class UserList extends Vue {
  createUserModal = false;

  editUserRoleModal = false;

  userRoleDeleteModal = false;

  userRole = null;

  userRoleToCreate = null;

  userRoleToDelete = null;

  pointOfSaleFilter = [];

  userRoleFilter = 0;

  userFilter = ''

  userRoleLevelFilter = 'all';

  userActiveFilter = 'all';

  normalizedNamesMap = new Map();

  async created() {
    await this.userRoleListRequest();
    this.initializeNormalizedNamesMap();
  }

  get filteredUserRoleList() {
    let userRoleListFiltered = this.userRoleList;
    if (this.pointOfSaleFilter.length > 0) {
      userRoleListFiltered = userRoleListFiltered.filter((item) => {
        const pos = item.allowedPointsOfSales.filter((each) => this.pointOfSaleFilter.includes(each));
        return pos.length > 0;
      });
    }
    if (this.userRoleFilter !== 0) {
      userRoleListFiltered = userRoleListFiltered.filter((item) => item.role === this.userRoleFilter);
    }

    if (this.userRoleLevelFilter !== 'all') {
      userRoleListFiltered = userRoleListFiltered.filter((item) => {
        if (this.userRoleLevelFilter === 'same') {
          return item.roleLevel === this.userSettings.roleLevel;
        }
        return item.roleLevel > this.userSettings.roleLevel;
      });
    }

    if (this.userActiveFilter !== 'all') {
      userRoleListFiltered = userRoleListFiltered.filter((item) => item.user.isActive === (this.userActiveFilter === 'active'));
    }

    if (this.userFilter !== '' && this.normalizedNamesMap.size) {
      const token = normalizeStr(this.userFilter.trim());
      userRoleListFiltered = userRoleListFiltered
        .filter((el) => this.normalizedNamesMap.get(el.user.username)?.normalizedUsername?.includes(token));
    }
    return userRoleListFiltered;
  }

  get userRoleToDeleteTitle() {
    return this.userRoleToDelete ? this.userRoleToDelete.user.username : '';
  }

  onEdit(userRole) {
    const props = userRole.serialize();
    this.userRole = UserRole.toInstance(props);
    this.editUserRoleModal = true;
  }

  onDelete(userRole) {
    this.userRoleToDelete = userRole;
    this.userRoleDeleteModal = true;
  }

  async onRegeneratePassword(userRole) {
    try {
      await this.regeneratePasswordUserRoleRequest(userRole);
      this.addNotification(createSuccessNotification(this.$t('regeneratePassUserRoleSuccess', [userRole.user.username]), null, 3000));
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('regeneratePassUserRoleError', [userRole.user.username])));
    }
  }

  initializeNormalizedNamesMap() {
    this.userRoleList.forEach((el) => {
      this.normalizedNamesMap.set(el.user.username, {
        normalizedUsername: normalizeStr(el.user.username)
      });
    });
  }

  openModal() {
    this.userRoleToCreate = UserRole.toInstance();
    this.createUserModal = true;
  }

  async onConfirmUserRole(userRole) {
    try {
      await this.createUserRoleRequest(userRole);
      this.addNotification(createSuccessNotification(this.$t('createUserRoleSuccess', [userRole.user.username]), null, 3000));
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('createUserRoleError', [userRole.user.username])));
    } finally {
      this.onCloseModal();
      this.userRoleToCreate = null;
    }
  }

  async onEditUserRole(userRole) {
    try {
      await this.updateUserRoleRequest(userRole);
      this.addNotification(createSuccessNotification(this.$t('editUserRoleSuccess', [userRole.user.username]), null, 3000));
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('editUserRoleError', [userRole.user.username])));
    } finally {
      this.onCloseModal();
      this.userRole = null;
    }
  }

  async onDeleteUserRole() {
    try {
      await this.deleteUserRoleRequest(this.userRoleToDelete);
      this.addNotification(createSuccessNotification(this.$t('deleteUserRoleSuccess', [this.userRoleToDelete.user.username]), null, 3000));
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('deleteUserRoleError', [this.userRoleToDelete.user.username])));
    } finally {
      this.onCloseModal();
      this.userRoleToDelete = null;
    }
  }

  onCloseModal() {
    this.createUserModal = false;
    this.editUserRoleModal = false;
    this.userRoleDeleteModal = false;
  }

  onPointOfSaleFilter(string) {
    this.pointOfSaleFilter = string;
  }

  onUserRoleFilter(string) {
    this.userRoleFilter = string;
  }

  onUserRoleLevelFilter(string) {
    this.userRoleLevelFilter = string;
  }

  onUserActiveFilter(string) {
    this.userActiveFilter = string;
  }

  onUserSearch(string) {
    this.userFilter = string;
  }

  @Watch('createUserRoleSuccess')
  async onCreateUserRoleSuccessChange(newValue) {
    if (newValue) {
      await this.userRoleListRequest();
    }
  }

  @Watch('updateUserRoleSuccess')
  async onUpdateUserRoleSuccessChange(newValue) {
    if (newValue) {
      await this.userRoleListRequest();
    }
  }

  @Watch('deleteUserRoleSuccess')
  async onDeleteUserRoleSuccessChange(newValue) {
    if (newValue) {
      await this.userRoleListRequest();
    }
  }
}
</script>
<style scoped>
.empty-data {
  text-align: center;
}

.plus-icon {
  float: right;
}
</style>
